import { canonicalTankQrPayload } from "./qrPayload";

export const QR_VERSION_1_SIZE = 21;
export const QR_QUIET_ZONE_MODULES = 4;
export const QR_FIXED_MASK_PATTERN = 0;

const DATA_CODEWORDS = 19;
const ERROR_CORRECTION_CODEWORDS = 7;
const GF_PRIMITIVE = 0x11d;
const FORMAT_GENERATOR = 0x537;
const FORMAT_MASK = 0x5412;

type MutableMatrix = Array<Array<boolean | null>>;

const gfExp = new Array<number>(512).fill(0);
const gfLog = new Array<number>(256).fill(0);

{
  let value = 1;
  for (let index = 0; index < 255; index += 1) {
    gfExp[index] = value;
    gfLog[value] = index;
    value <<= 1;
    if ((value & 0x100) !== 0) value ^= GF_PRIMITIVE;
  }

  for (let index = 255; index < gfExp.length; index += 1) {
    gfExp[index] = gfExp[index - 255] ?? 0;
  }
}

function putBits(bits: boolean[], value: number, length: number) {
  for (let shift = length - 1; shift >= 0; shift -= 1) {
    bits.push(((value >>> shift) & 1) === 1);
  }
}

function bitsToBytes(bits: readonly boolean[]): number[] {
  const bytes: number[] = [];

  for (let offset = 0; offset < bits.length; offset += 8) {
    let value = 0;
    for (let index = 0; index < 8; index += 1) {
      value = (value << 1) | (bits[offset + index] ? 1 : 0);
    }
    bytes.push(value);
  }

  return bytes;
}

function buildDataCodewords(payload: string): number[] {
  const bytes = Array.from(payload, (character) => character.charCodeAt(0));
  const bits: boolean[] = [];

  // Version 1-L, byte mode.
  putBits(bits, 0b0100, 4);
  putBits(bits, bytes.length, 8);
  for (const byte of bytes) putBits(bits, byte, 8);

  const bitLimit = DATA_CODEWORDS * 8;
  const terminatorLength = Math.min(4, bitLimit - bits.length);
  for (let index = 0; index < terminatorLength; index += 1) bits.push(false);
  while (bits.length % 8 !== 0) bits.push(false);

  const data = bitsToBytes(bits);
  const pads = [0xec, 0x11];
  let padIndex = 0;

  while (data.length < DATA_CODEWORDS) {
    data.push(pads[padIndex % pads.length] ?? 0xec);
    padIndex += 1;
  }

  return data;
}

function polynomialMultiply(left: readonly number[], right: readonly number[]): number[] {
  const output = new Array<number>(left.length + right.length - 1).fill(0);

  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    const leftValue = left[leftIndex] ?? 0;
    if (leftValue === 0) continue;

    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const rightValue = right[rightIndex] ?? 0;
      if (rightValue === 0) continue;

      const exponent = (gfLog[leftValue] ?? 0) + (gfLog[rightValue] ?? 0);
      const outputIndex = leftIndex + rightIndex;
      output[outputIndex] = (output[outputIndex] ?? 0) ^ (gfExp[exponent] ?? 0);
    }
  }

  return output;
}

function errorCorrectionCodewords(data: readonly number[]): number[] {
  let generator = [1];

  for (let index = 0; index < ERROR_CORRECTION_CODEWORDS; index += 1) {
    generator = polynomialMultiply(generator, [1, gfExp[index] ?? 0]);
  }

  const working = [...data, ...new Array<number>(ERROR_CORRECTION_CODEWORDS).fill(0)];

  for (let offset = 0; offset < data.length; offset += 1) {
    const coefficient = working[offset] ?? 0;
    if (coefficient === 0) continue;

    const coefficientLog = gfLog[coefficient] ?? 0;
    for (let index = 0; index < generator.length; index += 1) {
      const generatorValue = generator[index] ?? 0;
      if (generatorValue === 0) continue;

      const workingIndex = offset + index;
      working[workingIndex] =
        (working[workingIndex] ?? 0) ^ (gfExp[coefficientLog + (gfLog[generatorValue] ?? 0)] ?? 0);
    }
  }

  return working.slice(-ERROR_CORRECTION_CODEWORDS);
}

function bchDigit(value: number): number {
  let digit = 0;
  let remaining = value;

  while (remaining !== 0) {
    digit += 1;
    remaining >>>= 1;
  }

  return digit;
}

function formatBits(maskPattern: number): number {
  // QR error correction level L is format value 01.
  const data = (1 << 3) | maskPattern;
  let remainder = data << 10;

  while (bchDigit(remainder) - bchDigit(FORMAT_GENERATOR) >= 0) {
    remainder ^= FORMAT_GENERATOR << (bchDigit(remainder) - bchDigit(FORMAT_GENERATOR));
  }

  return ((data << 10) | remainder) ^ FORMAT_MASK;
}

function setupFinder(matrix: MutableMatrix, row: number, column: number) {
  const size = matrix.length;

  for (let rowOffset = -1; rowOffset <= 7; rowOffset += 1) {
    const targetRow = row + rowOffset;
    if (targetRow < 0 || targetRow >= size) continue;

    for (let columnOffset = -1; columnOffset <= 7; columnOffset += 1) {
      const targetColumn = column + columnOffset;
      if (targetColumn < 0 || targetColumn >= size) continue;

      const dark =
        (rowOffset >= 0 && rowOffset <= 6 && (columnOffset === 0 || columnOffset === 6)) ||
        (columnOffset >= 0 && columnOffset <= 6 && (rowOffset === 0 || rowOffset === 6)) ||
        (rowOffset >= 2 && rowOffset <= 4 && columnOffset >= 2 && columnOffset <= 4);

      const target = matrix[targetRow];
      if (target) target[targetColumn] = dark;
    }
  }
}

function setupTiming(matrix: MutableMatrix) {
  const size = matrix.length;

  for (let row = 8; row < size - 8; row += 1) {
    if (matrix[row]?.[6] === null) {
      const target = matrix[row];
      if (target) target[6] = row % 2 === 0;
    }
  }

  for (let column = 8; column < size - 8; column += 1) {
    if (matrix[6]?.[column] === null) {
      const target = matrix[6];
      if (target) target[column] = column % 2 === 0;
    }
  }
}

function setupFormatInfo(matrix: MutableMatrix, maskPattern: number) {
  const size = matrix.length;
  const bits = formatBits(maskPattern);

  for (let index = 0; index < 15; index += 1) {
    const dark = ((bits >>> index) & 1) === 1;
    let row: number;

    if (index < 6) row = index;
    else if (index < 8) row = index + 1;
    else row = size - 15 + index;

    const target = matrix[row];
    if (target) target[8] = dark;
  }

  const formatRow = matrix[8];
  if (!formatRow) throw new Error("QR_MATRIX_INVALID");

  for (let index = 0; index < 15; index += 1) {
    const dark = ((bits >>> index) & 1) === 1;

    if (index < 8) {
      formatRow[size - index - 1] = dark;
    } else if (index < 9) {
      formatRow[15 - index] = dark;
    } else {
      formatRow[14 - index] = dark;
    }
  }

  const fixedRow = matrix[size - 8];
  if (fixedRow) fixedRow[8] = true;
}

function maskApplies(row: number, column: number): boolean {
  return (row + column) % 2 === 0;
}

function mapCodewords(matrix: MutableMatrix, codewords: readonly number[]) {
  const size = matrix.length;
  let direction = -1;
  let row = size - 1;
  let bitIndex = 7;
  let byteIndex = 0;

  for (let originalColumn = size - 1; originalColumn > 0; originalColumn -= 2) {
    const column = originalColumn <= 6 ? originalColumn - 1 : originalColumn;

    while (true) {
      for (const targetColumn of [column, column - 1]) {
        const targetRow = matrix[row];
        if (targetRow?.[targetColumn] === null) {
          let dark = false;
          const byte = codewords[byteIndex];

          if (byte !== undefined) {
            dark = ((byte >>> bitIndex) & 1) === 1;
          }

          if (maskApplies(row, targetColumn)) dark = !dark;
          targetRow[targetColumn] = dark;

          bitIndex -= 1;
          if (bitIndex === -1) {
            byteIndex += 1;
            bitIndex = 7;
          }
        }
      }

      row += direction;
      if (row < 0 || row >= size) {
        row -= direction;
        direction = -direction;
        break;
      }
    }
  }
}

export function buildTankQrMatrix(tankCode: string): boolean[][] {
  const payload = canonicalTankQrPayload(tankCode);
  const data = buildDataCodewords(payload);
  const codewords = [...data, ...errorCorrectionCodewords(data)];

  const matrix: MutableMatrix = Array.from({ length: QR_VERSION_1_SIZE }, () =>
    new Array<boolean | null>(QR_VERSION_1_SIZE).fill(null),
  );

  setupFinder(matrix, 0, 0);
  setupFinder(matrix, QR_VERSION_1_SIZE - 7, 0);
  setupFinder(matrix, 0, QR_VERSION_1_SIZE - 7);
  setupTiming(matrix);
  setupFormatInfo(matrix, QR_FIXED_MASK_PATTERN);
  mapCodewords(matrix, codewords);

  return matrix.map((row) =>
    row.map((value) => {
      if (value === null) throw new Error("QR_MATRIX_INCOMPLETE");
      return value;
    }),
  );
}
